/**
 * CooCommandCenter.js — CEO's single-pane oversight surface.
 *
 * Sections:
 *   - COO Charter (verbatim, locked)
 *   - Solicitations feed (ingest + fit-score ranked)
 *   - Proposal queue (auto-generate, review, dispatch)
 *   - Procurement live line-item tracker (composed -> consumed)
 *   - Compliance sealer (NSPIRE + HUD-VASH)
 */
import React, { useEffect, useState } from "react";
import CooIdentityRibbon from "./components/CooIdentityRibbon";

const BACKEND_URL = "";
const CYAN = "#00CFCF"; const CHROME = "#A0A0A0"; const INK = "#F5F5F7";

const page = { padding: "clamp(16px,3vw,32px)", background: "#020A1F", minHeight: "100vh", color: INK, fontFamily: "system-ui, -apple-system, sans-serif" };
const h1 = { fontSize: "clamp(24px,4vw,42px)", fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 6 };
const sub = { color: CHROME, fontSize: 13, letterSpacing: "0.05em", marginBottom: 18 };
const card = { background: "rgba(255,255,255,0.03)", border: `1px solid ${CHROME}33`, borderRadius: 12, padding: "clamp(14px,2.5vw,22px)", marginBottom: 18 };
const btn = { background: CYAN, color: "#020A1F", border: 0, padding: "8px 14px", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer", letterSpacing: "0.03em" };
const btnGhost = { ...btn, background: "transparent", color: INK, border: `1px solid ${CHROME}55` };
const kpi = { background: "rgba(0,0,0,0.25)", padding: 14, borderRadius: 8, border: `1px solid ${CHROME}22` };
const th = { textAlign: "left", padding: "8px 6px", fontSize: 11, letterSpacing: "0.1em", color: CHROME, borderBottom: `1px solid ${CHROME}33` };
const td = { padding: "10px 6px", fontSize: 12, borderBottom: `1px solid ${CHROME}22` };

const STAGE_COLOR = {
  composed: "#8892b0",
  dispatched: "#f5a942",
  confirmed: "#4fa8f0",
  delivered: "#8f5cf5",
  consumed: CYAN,
};

export default function CooCommandCenter() {
  const [summary, setSummary] = useState(null);
  const [solicitations, setSolicitations] = useState([]);
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);
  const [trackerPO, setTrackerPO] = useState("");
  const [tracker, setTracker] = useState([]);

  const load = async () => {
    const [sum, sols, q] = await Promise.all([
      fetch(`${BACKEND_URL}/api/coo/command-center/summary`).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/coo/solicitations?min_fit=0&limit=100`).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/coo/proposals/queue?limit=100`).then(r => r.json()),
    ]);
    setSummary(sum);
    setSolicitations(sols.solicitations || []);
    setQueue(q.queue || []);
  };

  useEffect(() => { load(); }, []);

  const ingest = async () => {
    setBusy("ingest");
    const r = await fetch(`${BACKEND_URL}/api/coo/solicitations/ingest`, { method: "POST" }).then(x => x.json());
    setMessage(`✓ Solicitations ingested — seed: ${r.seed.inserted} new / ${r.seed.updated} updated · live SAM.gov: ${r.live_sam_gov.status}`);
    setBusy(null); load();
  };

  const autoGenerate = async () => {
    setBusy("autogen");
    const r = await fetch(`${BACKEND_URL}/api/coo/proposals/auto-generate?min_fit=55`, { method: "POST" }).then(x => x.json());
    setMessage(`✓ ${r.generated} proposal(s) drafted for ${r.eligible_solicitations} eligible solicitation(s) at fit >= ${r.min_fit}`);
    setBusy(null); load();
  };

  const dispatchProposal = async (proposalId) => {
    setBusy(proposalId);
    const r = await fetch(`${BACKEND_URL}/api/coo/proposals/${proposalId}/dispatch`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ dispatched_by: "ceo_command_center" }),
    }).then(x => x.json());
    setMessage(`✓ Proposal ${r.proposal_id} dispatched · ${new Date(r.dispatched_at).toLocaleString()}`);
    setBusy(null); load();
  };

  const [trackerMessage, setTrackerMessage] = useState(null);

  const loadTracker = async () => {
    if (!trackerPO.trim()) return;
    setTrackerMessage(null);
    const r = await fetch(`${BACKEND_URL}/api/coo/procurement/tracker/${trackerPO.trim()}`);
    if (r.status === 404) {
      setTracker([]);
      setTrackerMessage(`⚑ No PO found matching '${trackerPO.trim()}'. Check the PO number in the ledger.`);
      return;
    }
    const d = await r.json();
    setTracker(d.lines || []);
    if ((d.lines || []).length === 0) {
      setTrackerMessage(`⚑ PO ${trackerPO.trim()} exists but has no line items.`);
    }
  };

  const advanceLine = async (po, idx) => {
    setBusy(`advance-${po}-${idx}`);
    await fetch(`${BACKEND_URL}/api/coo/procurement/tracker/${po}/line/${idx}/advance`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ by: "ceo" }),
    });
    setBusy(null); loadTracker();
  };

  if (!summary) return <div style={page}>Loading COO Command Center…</div>;

  const s = summary;

  return (
    <div style={page} data-testid="coo-command-center">
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        <div style={{ fontSize: 11, letterSpacing: "0.25em", color: CYAN }}>MOOVIN FORWARD LLC · ROI STABILIZATION FUND</div>
        <h1 style={h1}>COO Command Center</h1>
        <div style={sub}>CEO-facing oversight — solicitations, proposals, procurement, compliance.</div>

        <CooIdentityRibbon />

        {message && (
          <div style={{ ...card, borderColor: `${CYAN}66`, color: CYAN }} data-testid="cmd-message">{message}</div>
        )}

        {/* KPI STRIP */}
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", marginBottom: 18 }}>
          <div style={kpi} data-testid="kpi-solicitations">
            <div style={{ fontSize: 10, color: CHROME, letterSpacing: "0.1em" }}>SOURCED SOLICITATIONS</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: CYAN }}>{s.solicitations.total}</div>
            <div style={{ fontSize: 11, color: CHROME }}>{s.solicitations.high_fit_over_70} high-fit (&ge;70)</div>
          </div>
          <div style={kpi} data-testid="kpi-pipeline">
            <div style={{ fontSize: 10, color: CHROME, letterSpacing: "0.1em" }}>PIPELINE VALUE</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: CYAN }}>${(s.solicitations.pipeline_value_usd || 0).toLocaleString()}</div>
          </div>
          <div style={kpi} data-testid="kpi-queue">
            <div style={{ fontSize: 10, color: CHROME, letterSpacing: "0.1em" }}>PROPOSAL QUEUE</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: CYAN }}>{s.proposal_queue.total}</div>
            <div style={{ fontSize: 11, color: CHROME }}>{s.proposal_queue.queued} queued · {s.proposal_queue.dispatched} sent</div>
          </div>
          <div style={kpi} data-testid="kpi-awards">
            <div style={{ fontSize: 10, color: CHROME, letterSpacing: "0.1em" }}>AWARDED / LOST</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: CYAN }}>{s.proposal_queue.awarded} / {s.proposal_queue.lost}</div>
          </div>
          <div style={kpi} data-testid="kpi-procurement">
            <div style={{ fontSize: 10, color: CHROME, letterSpacing: "0.1em" }}>PROCUREMENT LINES</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: CYAN }}>{s.procurement_transparency.total_lines}</div>
          </div>
        </div>

        {/* SOLICITATIONS */}
        <div style={card} data-testid="solicitations-feed">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN }}>PILLAR 1 · BUSINESS DEVELOPMENT</div>
              <h2 style={{ margin: "4px 0 0 0", fontSize: 20, fontWeight: 700 }}>Sourced Contract Solicitations</h2>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={btn} onClick={ingest} disabled={busy === "ingest"} data-testid="btn-ingest-solicitations">
                {busy === "ingest" ? "Ingesting…" : "🎯 Ingest Feed"}
              </button>
              <button style={btnGhost} onClick={autoGenerate} disabled={busy === "autogen"} data-testid="btn-auto-generate">
                {busy === "autogen" ? "Drafting…" : "📝 Auto-Generate Proposals"}
              </button>
            </div>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>FIT</th>
                <th style={th}>SOLICITATION</th>
                <th style={th}>AGENCY</th>
                <th style={th}>VALUE</th>
                <th style={th}>DEADLINE</th>
              </tr>
            </thead>
            <tbody>
              {solicitations.slice(0, 15).map(sol => (
                <tr key={sol.solicitation_id} data-testid={`sol-row-${sol.solicitation_id}`}>
                  <td style={{ ...td, color: (sol.fit_score || 0) >= 70 ? CYAN : CHROME, fontWeight: 700 }}>{sol.fit_score}</td>
                  <td style={td}><strong>{sol.title}</strong><div style={{ color: CHROME, fontSize: 11 }}>{sol.solicitation_id}</div></td>
                  <td style={td}>{sol.agency}</td>
                  <td style={td}>${(sol.estimated_value_usd || 0).toLocaleString()}</td>
                  <td style={td}>{sol.response_deadline ? new Date(sol.response_deadline).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
              {solicitations.length === 0 && (
                <tr><td colSpan={5} style={{ ...td, color: CHROME, textAlign: "center", padding: 22 }}>
                  No solicitations yet. Click <strong>Ingest Feed</strong> above to source the current DFW feed.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PROPOSAL QUEUE */}
        <div style={card} data-testid="proposal-queue">
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN }}>PILLAR 2 · PROPOSAL DISPATCH</div>
          <h2 style={{ margin: "4px 0 12px 0", fontSize: 20, fontWeight: 700 }}>Proposal Queue · CEO Review</h2>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>PROPOSAL</th>
                <th style={th}>FIT</th>
                <th style={th}>REC. BID</th>
                <th style={th}>STATUS</th>
                <th style={th}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {queue.map(p => (
                <tr key={p.proposal_id} data-testid={`queue-row-${p.proposal_id}`}>
                  <td style={td}><strong>{p.proposal_id}</strong><div style={{ color: CHROME, fontSize: 11 }}>{p.solicitation_title}</div></td>
                  <td style={{ ...td, color: (p.fit_score || 0) >= 70 ? CYAN : CHROME, fontWeight: 700 }}>{p.fit_score}</td>
                  <td style={td}>${(p.recommended_bid_usd || 0).toLocaleString()}</td>
                  <td style={{ ...td, color: p.status === "dispatched" ? CYAN : p.status === "awarded" ? "#4fa8f0" : p.status === "lost" ? "#f04040" : CHROME }}>
                    {(p.status || "queued").toUpperCase()}
                  </td>
                  <td style={td}>
                    {p.status === "queued" && (
                      <button style={btn} onClick={() => dispatchProposal(p.proposal_id)}
                        disabled={busy === p.proposal_id}
                        data-testid={`btn-dispatch-${p.proposal_id}`}>
                        {busy === p.proposal_id ? "Sending…" : "Dispatch →"}
                      </button>
                    )}
                    {p.status !== "queued" && <span style={{ color: CHROME, fontSize: 11 }}>{p.dispatched_at ? new Date(p.dispatched_at).toLocaleString() : ""}</span>}
                  </td>
                </tr>
              ))}
              {queue.length === 0 && (
                <tr><td colSpan={5} style={{ ...td, color: CHROME, textAlign: "center", padding: 22 }}>
                  No drafted proposals. Ingest the feed then click <strong>Auto-Generate Proposals</strong>.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PROCUREMENT TRACKER */}
        <div style={card} data-testid="procurement-tracker">
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN }}>PILLAR 3 · PROCUREMENT TRANSPARENCY</div>
          <h2 style={{ margin: "4px 0 12px 0", fontSize: 20, fontWeight: 700 }}>Live Line-Item Tracker</h2>
          <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(5, 1fr)", marginBottom: 14 }}>
            {["composed", "dispatched", "confirmed", "delivered", "consumed"].map(stage => (
              <div key={stage} style={{ ...kpi, borderLeft: `3px solid ${STAGE_COLOR[stage]}` }} data-testid={`stage-tile-${stage}`}>
                <div style={{ fontSize: 10, color: CHROME, letterSpacing: "0.1em" }}>{stage.toUpperCase()}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: STAGE_COLOR[stage] }}>
                  {s.procurement_transparency.counts_by_stage?.[stage] || 0}
                </div>
                <div style={{ fontSize: 11, color: CHROME }}>${(s.procurement_transparency.dollars_by_stage?.[stage] || 0).toLocaleString()}</div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            <input placeholder="Enter PO Number (e.g. PO-XXXXXXXXXX)"
              value={trackerPO} onChange={e => setTrackerPO(e.target.value)}
              data-testid="tracker-po-input"
              style={{ flex: 1, minWidth: 220, background: "rgba(0,0,0,0.35)", border: `1px solid ${CHROME}44`, color: INK, padding: "8px 10px", borderRadius: 6 }} />
            <button style={btn} onClick={loadTracker} data-testid="btn-load-tracker">Load PO Lines</button>
          </div>
          {trackerMessage && (
            <div style={{ marginBottom: 8, color: "#f5a942", fontSize: 12 }} data-testid="tracker-message">
              {trackerMessage}
            </div>
          )}
          {tracker.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>#</th>
                  <th style={th}>DESCRIPTION</th>
                  <th style={th}>QTY</th>
                  <th style={th}>TOTAL</th>
                  <th style={th}>STAGE</th>
                  <th style={th}>ADVANCE</th>
                </tr>
              </thead>
              <tbody>
                {tracker.map(l => (
                  <tr key={l.line_index} data-testid={`tracker-line-${l.line_index}`}>
                    <td style={td}>{l.line_index + 1}</td>
                    <td style={td}>{l.description}</td>
                    <td style={td}>{l.quantity}</td>
                    <td style={td}>${(l.line_total || 0).toLocaleString()}</td>
                    <td style={{ ...td, color: STAGE_COLOR[l.current_stage] || CHROME, fontWeight: 700 }}>{(l.current_stage || "").toUpperCase()}</td>
                    <td style={td}>
                      {l.current_stage !== "consumed" && (
                        <button style={btn} onClick={() => advanceLine(l.po_number, l.line_index)}
                          disabled={busy === `advance-${l.po_number}-${l.line_index}`}
                          data-testid={`btn-advance-${l.line_index}`}>
                          Advance →
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* COMPLIANCE */}
        <ComplianceSealer />

        {/* DESIGN GOVERNANCE (default-to-execute) */}
        <DesignGovernancePanel />

        {/* LIVE SIMULATION · INTERACTIVE COO */}
        <LiveSimulationPanel />

        {/* PAYMENT TRANSPARENCY · READ-ONLY */}
        <PaymentTransparencyPanel />

      </div>
    </div>
  );
}

function PaymentTransparencyPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/mf/financial/transparency`).then(r => r.json()).then(setData).catch(() => {});
  }, []);
  if (!data) return null;
  const t = data.transparency || {};
  const p = data.late_penalty_schedule || {};
  const s = data.signoff || {};
  return (
    <div style={card} data-testid="payment-transparency-panel">
      <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN }}>PILLAR 7 · PAYMENT SCHEDULE TRANSPARENCY</div>
      <h2 style={{ margin: "4px 0 12px 0", fontSize: 20, fontWeight: 700 }}>Why the Structure Works This Way</h2>

      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
        <div style={{ ...kpi, borderLeft: `3px solid ${CYAN}` }} data-testid="transparency-packet-1">
          <div style={{ fontSize: 10, color: CYAN, letterSpacing: "0.15em" }}>PACKET 1 · UPFRONT</div>
          <div style={{ fontSize: 13, marginTop: 8, lineHeight: 1.55 }}>{t.packet_1_justification}</div>
        </div>
        <div style={{ ...kpi, borderLeft: `3px solid ${CYAN}` }} data-testid="transparency-packet-2">
          <div style={{ fontSize: 10, color: CYAN, letterSpacing: "0.15em" }}>PACKET 2 · 72H POST SIGN-OFF</div>
          <div style={{ fontSize: 13, marginTop: 8, lineHeight: 1.55 }}>{t.packet_2_justification}</div>
        </div>
      </div>

      <div style={{ ...kpi, marginTop: 12, borderLeft: `3px solid #f5a942` }} data-testid="transparency-signoff">
        <div style={{ fontSize: 10, color: "#f5a942", letterSpacing: "0.15em" }}>AUTHORIZED SIGN-OFF VISIBILITY</div>
        <div style={{ fontSize: 13, marginTop: 8, lineHeight: 1.55 }}>{t.signoff_visibility}</div>
        <div style={{ fontSize: 11, color: CHROME, marginTop: 6 }}>
          Authorized roles: {(s.authorized_roles || []).join(" · ")}
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 10, color: "#f04040", letterSpacing: "0.15em", marginBottom: 8 }}>LATE PENALTY DISCLOSURE · &gt;72H WINDOW</div>
        <div style={{ fontSize: 13, color: CHROME, marginBottom: 10 }}>{t.penalty_disclosure}</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>TIER</th>
              <th style={th}>WINDOW PAST 72H</th>
              <th style={th}>RATE / DAY</th>
              <th style={th}>NOTE</th>
            </tr>
          </thead>
          <tbody>
            {["tier_1", "tier_2", "tier_3"].map(tk => {
              const row = p[tk] || {};
              return (
                <tr key={tk} data-testid={`penalty-${tk}`}>
                  <td style={{ ...td, color: CYAN, fontWeight: 700 }}>{tk.replace("_", " ").toUpperCase()}</td>
                  <td style={td}>{row.window_hours_after_72h ? `${row.window_hours_after_72h} h` : "—"}</td>
                  <td style={{ ...td, color: "#f04040", fontWeight: 700 }}>{row.penalty_pct_per_day ? `${row.penalty_pct_per_day}%` : "—"}</td>
                  <td style={{ ...td, color: CHROME }}>{row.description || row.escalation || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ marginTop: 8, fontSize: 11, color: CHROME }}>
          Compounding: {p.compounding} · Waiver: {p.waiver}
        </div>
      </div>
    </div>
  );
}

function LiveSimulationPanel() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const refresh = async () => {
    const r = await fetch(`${BACKEND_URL}/api/live-simulation/current`).then(x => x.json());
    setState(r);
  };
  useEffect(() => { refresh(); }, []);

  const doStart = async () => {
    setBusy(true);
    const r = await fetch(`${BACKEND_URL}/api/live-simulation/start`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ started_by: "ceo_command_center" }),
    }).then(x => x.json());
    setState(r); setMsg("✓ Live simulation started."); setBusy(false);
  };

  const doAdvance = async () => {
    setBusy(true);
    const r = await fetch(`${BACKEND_URL}/api/live-simulation/advance`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }).then(x => x.json());
    setState(r);
    setMsg(r.status === "completed" ? "✓ Simulation complete." : `✓ Advanced to step ${r.current_step}.`);
    setBusy(false);
  };

  const doReset = async () => {
    setBusy(true);
    const r = await fetch(`${BACKEND_URL}/api/live-simulation/reset`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}),
    }).then(x => x.json());
    setState(r); setMsg("✓ Reset · back to step 1."); setBusy(false);
  };

  if (!state) return null;

  const step = state.active_step || {};
  const pct = Math.round(((state.current_step || 1) / (state.total_steps || 6)) * 100);

  return (
    <div style={card} data-testid="live-simulation-panel">
      <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN }}>PILLAR 6 · LIVE INTERACTIVE SIMULATION</div>
      <h2 style={{ margin: "4px 0 6px 0", fontSize: 20, fontWeight: 700 }}>
        {state.property_address}
      </h2>
      <div style={{ fontSize: 12, color: CHROME, marginBottom: 12, fontStyle: "italic" }} data-testid="coo-speaking">
        {state.coo_speaking}
      </div>

      <div style={{ background: "rgba(0,0,0,0.25)", borderRadius: 8, height: 8, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ height: "100%", width: `${pct}%`, background: CYAN, transition: "width 0.4s" }} data-testid="sim-progress" />
      </div>

      <div style={{ ...kpi, marginBottom: 12, borderLeft: `3px solid ${CYAN}` }}>
        <div style={{ fontSize: 10, color: CYAN, letterSpacing: "0.15em" }}>
          STEP {step.step} / {state.total_steps} · {(step.title || "").toUpperCase()}
        </div>
        <div style={{ fontSize: 14, marginTop: 8, lineHeight: 1.5 }} data-testid="sim-prompt">
          {step.prompt}
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: CHROME }}>
          <strong style={{ color: CYAN }}>Validates:</strong> {step.validates}
        </div>
        <div style={{ marginTop: 4, fontSize: 12, color: CHROME }}>
          <strong style={{ color: CYAN }}>Next action:</strong> {step.next_action}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button style={{ ...btn, opacity: busy ? 0.5 : 1 }} onClick={doAdvance}
          disabled={busy || state.status === "completed"}
          data-testid="btn-sim-advance">
          {state.status === "completed" ? "Complete ✓" : "Advance to Next Step →"}
        </button>
        <button style={btnGhost} onClick={doStart} disabled={busy} data-testid="btn-sim-start">Start Fresh</button>
        <button style={btnGhost} onClick={doReset} disabled={busy} data-testid="btn-sim-reset">Reset</button>
      </div>

      {msg && <div style={{ marginTop: 10, color: CYAN, fontSize: 12 }} data-testid="sim-msg">{msg}</div>}

      {/* Steps overview */}
      <div style={{ marginTop: 16, display: "grid", gap: 6, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
        {(state.steps_overview || []).map(s => {
          const done = (state.completed_steps || []).some(c => c.step === s.step);
          const active = s.step === state.current_step;
          return (
            <div key={s.step} style={{
              padding: 8, borderRadius: 6, fontSize: 11,
              background: active ? "rgba(0,207,207,0.12)" : "rgba(255,255,255,0.02)",
              border: `1px solid ${active ? CYAN + "77" : CHROME + "22"}`,
              color: done ? CYAN : active ? INK : CHROME,
            }} data-testid={`sim-step-tile-${s.step}`}>
              <div style={{ fontSize: 9, letterSpacing: "0.1em", opacity: 0.7 }}>
                STEP {s.step}{done ? " · DONE" : active ? " · ACTIVE" : ""}
              </div>
              <div style={{ fontWeight: 700, marginTop: 2 }}>{s.title}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DesignGovernancePanel() {
  const [variations, setVariations] = useState([]);
  const [catalogSize, setCatalogSize] = useState(0);
  const [runs, setRuns] = useState(null);

  const refresh = () => {
    fetch(`${BACKEND_URL}/api/design/variations?limit=20`)
      .then(r => r.json()).then(d => setVariations(d.variations || []));
    fetch(`${BACKEND_URL}/api/procurement/catalog`)
      .then(r => r.json()).then(d => setCatalogSize(d.size || 0));
  };

  useEffect(() => { refresh(); }, []);

  const sweep = async () => {
    const r = await fetch(`${BACKEND_URL}/api/design/sweep-expired`, { method: "POST" }).then(x => x.json());
    setRuns(r);
    refresh();
  };

  const timeLeft = (iso) => {
    if (!iso) return "—";
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return "elapsed";
    const h = Math.floor(ms / 3600000); const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}m`;
  };

  return (
    <div style={card} data-testid="design-governance-panel">
      <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN }}>PILLAR 5 · DEFAULT-TO-EXECUTE DESIGN GOVERNANCE</div>
      <h2 style={{ margin: "4px 0 12px 0", fontSize: 20, fontWeight: 700 }}>Design Variations · Review Windows</h2>
      <div style={{ fontSize: 12, color: CHROME, marginBottom: 10 }}>
        Silence == full approval. Variations under review auto-lock and dispatch procurement when the review window elapses.
        Wholesale catalog: <span style={{ color: CYAN }}>{catalogSize}</span> SKU(s) · 2-3 day US shipping.
      </div>
      <button style={btn} onClick={sweep} data-testid="btn-sweep-expired">Run Sweep Now →</button>
      {runs && (
        <div style={{ marginTop: 8, fontSize: 12, color: CYAN }} data-testid="sweep-result">
          ✓ Sweep complete — {runs.expired_count} expired variation(s) locked and dispatched.
        </div>
      )}
      {variations.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12 }}>
          <thead>
            <tr>
              <th style={th}>VARIATION</th>
              <th style={th}>STATUS</th>
              <th style={th}>MANIFEST</th>
              <th style={th}>REVIEW LEFT</th>
              <th style={th}>PROCUREMENT</th>
            </tr>
          </thead>
          <tbody>
            {variations.map(v => (
              <tr key={v.variation_id} data-testid={`variation-row-${v.variation_id}`}>
                <td style={td}><strong>{v.variation_name}</strong><div style={{ color: CHROME, fontSize: 11 }}>{v.variation_id}</div></td>
                <td style={{ ...td, color: v.status === "locked" ? CYAN : v.status === "under_review" ? "#f5a942" : CHROME, fontWeight: 700 }}>
                  {(v.status || "").toUpperCase()}{v.auto_locked ? " (auto)" : ""}
                </td>
                <td style={td}>{(v.manifest || []).length} line(s)</td>
                <td style={td}>{v.status === "under_review" ? timeLeft(v.review_closes_at) : "—"}</td>
                <td style={{ ...td, color: v.procurement_dispatched ? CYAN : CHROME }}>
                  {v.procurement_dispatched ? "✓ dispatched" : "pending"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ComplianceSealer() {
  const [standard, setStandard] = useState("universal");
  const [checklist, setChecklist] = useState([]);
  const [address, setAddress] = useState("");
  const [inspector, setInspector] = useState("");
  const [findings, setFindings] = useState({});
  const [result, setResult] = useState(null);
  const [notes, setNotes] = useState({});

  useEffect(() => {
    let path = "/api/coo/compliance/nspire/checklist";
    if (standard === "hqs") path = "/api/coo/compliance/hud-vash/checklist";
    if (standard === "universal") path = "/api/compliance/universal/checklist";
    fetch(`${BACKEND_URL}${path}`).then(r => r.json()).then(d => setChecklist(d.checklist || []));
    setFindings({}); setResult(null); setNotes({});
  }, [standard]);

  const seal = async () => {
    const findingsPayload = checklist.map(c => ({
      id: c.id, verdict: findings[c.id] || "pass", note: notes[c.id] || "",
    }));
    let path = "/api/coo/compliance/nspire/seal";
    if (standard === "hqs") path = "/api/coo/compliance/hud-vash/seal";
    if (standard === "universal") path = "/api/compliance/universal/seal";
    const r = await fetch(`${BACKEND_URL}${path}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ property_address: address, inspector, findings: findingsPayload }),
    });
    const d = await r.json();
    setResult(d);
  };

  return (
    <div style={card} data-testid="compliance-sealer">
      <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN }}>PILLAR 4 · NATIVE COMPLIANCE</div>
      <h2 style={{ margin: "4px 0 12px 0", fontSize: 20, fontWeight: 700 }}>Inspection Sealer</h2>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <button style={standard === "universal" ? btn : btnGhost} onClick={() => setStandard("universal")} data-testid="btn-standard-universal">Universal Multi-Agency</button>
        <button style={standard === "nspire" ? btn : btnGhost} onClick={() => setStandard("nspire")} data-testid="btn-standard-nspire">HUD NSPIRE</button>
        <button style={standard === "hqs" ? btn : btnGhost} onClick={() => setStandard("hqs")} data-testid="btn-standard-hqs">HUD-VASH HQS</button>
      </div>
      <div style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr 1fr", marginBottom: 12 }}>
        <input placeholder="Property Address" value={address} onChange={e => setAddress(e.target.value)} data-testid="compliance-address"
          style={{ background: "rgba(0,0,0,0.35)", border: `1px solid ${CHROME}44`, color: INK, padding: "8px 10px", borderRadius: 6 }} />
        <input placeholder="Inspector Name" value={inspector} onChange={e => setInspector(e.target.value)} data-testid="compliance-inspector"
          style={{ background: "rgba(0,0,0,0.35)", border: `1px solid ${CHROME}44`, color: INK, padding: "8px 10px", borderRadius: 6 }} />
      </div>
      <div style={{ maxHeight: 400, overflowY: "auto", border: `1px solid ${CHROME}22`, borderRadius: 6, padding: 10, marginBottom: 12 }}>
        {checklist.map(c => (
          <div key={c.id} style={{ padding: "8px 4px", borderBottom: `1px solid ${CHROME}22` }} data-testid={`compliance-item-${c.id}`}>
            <div style={{ fontSize: 13, marginBottom: 4 }}>
              <strong style={{ color: standard === "nspire" ? CYAN : "#8f5cf5" }}>{c.area || c.category}</strong>
              <span style={{ color: CHROME, marginLeft: 6 }}>{c.item || c.requirement}</span>
              {c.severity_label && <span style={{ marginLeft: 6, fontSize: 10, color: "#f5a942" }}>[{c.severity_label}]</span>}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                style={findings[c.id] === "pass" ? { ...btn, padding: "4px 10px" } : { ...btnGhost, padding: "4px 10px" }}
                onClick={() => setFindings(f => ({ ...f, [c.id]: "pass" }))}
                data-testid={`compliance-${c.id}-pass`}>PASS</button>
              <button
                style={{
                  padding: "4px 10px", borderRadius: 6, fontWeight: 700, fontSize: 11, cursor: "pointer",
                  background: findings[c.id] === "fail" ? "#f04040" : "transparent",
                  color: INK, border: "1px solid #f04040",
                }}
                onClick={() => setFindings(f => ({ ...f, [c.id]: "fail" }))}
                data-testid={`compliance-${c.id}-fail`}>FAIL</button>
              {findings[c.id] === "fail" && (
                <input placeholder="Note" value={notes[c.id] || ""} onChange={e => setNotes(n => ({ ...n, [c.id]: e.target.value }))}
                  data-testid={`compliance-${c.id}-note`}
                  style={{ flex: 1, background: "rgba(0,0,0,0.35)", border: `1px solid ${CHROME}44`, color: INK, padding: "3px 8px", borderRadius: 4, fontSize: 11 }} />
              )}
            </div>
          </div>
        ))}
      </div>
      <button style={btn} onClick={seal} disabled={!address || !inspector} data-testid="btn-compliance-seal">
        Seal Inspection
      </button>
      {result && (
        <div style={{ ...card, marginTop: 12, borderColor: result.outcome === "pass" ? CYAN : "#f04040" }} data-testid="compliance-seal-result">
          <div style={{ fontSize: 13, color: result.outcome === "pass" ? CYAN : "#f04040", fontWeight: 700 }}>
            {result.outcome === "pass" ? "✓ INSPECTION PASSED" : "✗ INSPECTION FAILED"} · {result.inspection_id}
          </div>
          {result.counts && (
            <div style={{ fontSize: 12, color: CHROME, marginTop: 4 }}>
              Life-Threatening: {result.counts.life_threatening} · Severe: {result.counts.severe} · Moderate: {result.counts.moderate}
            </div>
          )}
          {result.per_standard && (
            <div style={{ marginTop: 8, display: "grid", gap: 6, gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
              {Object.entries(result.per_standard).map(([std, s]) => (
                <div key={std} style={{
                  padding: 8, borderRadius: 6, border: `1px solid ${s.passes ? CYAN : "#f04040"}55`,
                  background: s.passes ? "rgba(0,207,207,0.06)" : "rgba(240,64,64,0.06)",
                }} data-testid={`std-${std.toLowerCase()}`}>
                  <div style={{ fontSize: 10, color: CHROME, letterSpacing: "0.1em" }}>{std}</div>
                  <div style={{ fontSize: 13, color: s.passes ? CYAN : "#f04040", fontWeight: 700 }}>
                    {s.passes ? "✓ PASS" : `✗ FAIL · ${s.failed}/${s.total}`}
                  </div>
                </div>
              ))}
            </div>
          )}
          {result.voucher_placement_eligible !== undefined && (
            <div style={{ fontSize: 12, marginTop: 6, color: result.voucher_placement_eligible ? CYAN : "#f5a942" }}>
              {result.voucher_placement_eligible ? "✓ HQS-passable — voucher placement eligible" : "✗ Voucher placement blocked — correct then re-inspect"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
